import { StepType } from '@pushly/models/lib/enums/journeys'
import { isActionStep, JourneyNode } from '../types/journey-nodes'
// Type-only import: the shared feed-step params view model. `import type` keeps
// this compile-time-only so snapshot normalization does not pull the editor
// module (and its AQE feed-form helpers) into the runtime graph.
import type { FeedNotificationParams } from '../editors/send-feed-notification-editor/helpers'

/**
 * Produce a stable, canonical string snapshot of the journey steps for dirty
 * detection. Strips UI-only / enrichment-only fields so hydrating display data
 * after load does not make the journey appear dirty, and sorts by step id so
 * ordering differences do not register as changes.
 *
 * Extracted from `journeys-builder.tsx` so the normalization is unit-testable
 * in isolation (see `snapshot.spec.ts`).
 */
export function normalizeStepsForSnapshot(nodes: JourneyNode[]): string {
    const steps = nodes
        .filter((n) => n.data.step.type !== StepType.GLOBAL_TRIGGER)
        .map((n) => {
            const { step } = n.data
            // Only compare step-level data that matters for persistence:
            // id, type, configuration (without UI enrichments like the notification
            // object or the hydrated feed display object).
            if (isActionStep(step)) {
                // Assertion retained deliberately: normalization runs for EVERY action
                // step, not just feed steps, so there is no SEND_FEED_NOTIFICATION guard
                // to narrow through (adding one would change which steps get stripped).
                const params = step.configuration.params as FeedNotificationParams
                // Strip the enrichment-only fields that must NOT influence dirty
                // detection:
                //   - `notification`: the full display payload — replaced by a template
                //     fingerprint (`_tf`) so content edits are still detected without
                //     the bulky, hydration-dependent object triggering false positives.
                //   - `feed`: the Send Feed Notification step's hydrated feed display
                //     object. Only `feed_id` is persisted; the resolved feed name/type
                //     is added by node enrichment purely for the canvas subtitle, so it
                //     is dropped from the snapshot entirely.
                const { notification, feed, ...restParams } = params
                const contentFingerprint = notification?.template ? JSON.stringify(notification.template) : undefined
                return {
                    id: step.id,
                    type: step.type,
                    configuration: {
                        ...step.configuration,
                        params: { ...restParams, _tf: contentFingerprint },
                    },
                }
            }
            return { id: step.id, type: step.type, configuration: step.configuration }
        })
        .sort((a, b) => String(a.id).localeCompare(String(b.id)))
    return JSON.stringify(steps)
}
